import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

export function FAQ() {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      q: t('faq.q1', 'What makes the FIVE LENS framework different from standard Enneagram assessments?'),
      a: t('faq.a1', 'While traditional assessments focus solely on personality typing, the FIVE LENS integrates four additional dimensions, Personal Mastery, Emotional Resilience, Social Drives, and Energy Centres, providing a holistic view of human behaviour and leadership maturity.')
    },
    {
      q: t('faq.q2', 'Is the assessment scientifically validated?'),
      a: t('faq.a2', 'Yes. The FIVE LENS framework was developed over a decade of rigorous academic research, statistical validation, and real-world corporate application to ensure high reliability and construct validity.')
    },
    {
      q: t('faq.q3', 'Can this framework be used for entire teams, or is it just for individuals?'),
      a: t('faq.a3', 'Absolutely. We offer team dynamics reports that map the collective lenses of an entire group, uncovering hidden conflicts, highlighting complementary strengths, and providing a data-driven roadmap for better collaboration.')
    },
    {
      q: t('faq.q4', 'How long does the certification process take for practitioners?'),
      a: t('faq.a4', 'The full accreditation process typically takes 4 to 6 weeks, combining self-paced theoretical modules with intensive, interactive cohort sessions and supervised debriefs.')
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <section className="py-24 bg-ground border-t border-ground-deep">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-serif text-ink mb-4">
            {t('faq.title', 'Frequently Asked Questions')}
          </h2>
          <p className="text-ink/70/70">
            {t('faq.desc', 'Common queries about the FIVE LENS Enneagram development methodology.')}
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div key={index} className="border border-ground-deep bg-white transition-shadow hover:shadow-md hover:shadow-ink/5">
                <button
                  id={`faq-question-${index}`}
                  className="w-full text-left px-6 py-5 flex items-center justify-between focus:outline-none"
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${index}`}
                >
                  <span className="font-bold text-ink pr-8">{faq.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-rule-3 transition-transform duration-300 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      id={`faq-answer-${index}`}
                      role="region"
                      aria-labelledby={`faq-question-${index}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-5 text-ink/70/80 font-light leading-relaxed border-t border-ground-deep pt-4">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
